import PropTypes from "prop-types"
import { Link } from "react-router-dom"

import Image from "components/landing/image"
import Button from "components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "components/ui/carousel"
import { isTestMode } from "helpers/application"
import { cn } from "styles/utils"

// Keep these styles in sync with the loading shimmer `areas/landing/loaders/collection-menu.jsx`

const defaultBubbleStyle = {
  borderRadius: "50%",
  style: { height: "auto" },
}

const LandingCollectionMenu = ({ element, lazyload }) => {
  const properties = element?.data?.properties || {}
  const eventName = element?.event_name
  const {
    className: carouselClassName,
    opts,
    ...carouselProps
  } = properties?.carouselProps || {}
  const {
    className: carouselContentClassName,
    viewportClassName,
    ...carouselContentProps
  } = properties?.carouselContentProps || {}
  const { className: carouselItemClassName, ...carouselItemProps } =
    properties?.carouselItemProps || {}
  const { className: itemTitleClassName, ...itemTitleProps } =
    properties?.itemTitleProps || {}
  const { className: carouselControlsClassName } =
    properties?.carouselControlsProps || {}
  const { disableSkip } = properties || {}

  const handleClick = (title, url) => {
    if (eventName && !isTestMode) {
      dataLayer.push({
        clickedUrl: url,
        element: title,
        event: eventName,
      })
    }
  }

  return (
    <div {...properties?.containerProps}>
      <Carousel
        className={cn(element?.contain && "contained-wide", carouselClassName)}
        description={element?.data?.description}
        disableSkip={disableSkip}
        opts={{
          active: true,
          align: "start",
          breakpoints: {
            "(min-width: 1280px)": { active: false },
          },
          loop: false,
          slidesToScroll: "auto",
          ...opts,
        }}
        {...carouselProps}
      >
        <CarouselContent
          className={cn(
            "large:justify-center tablet:gap-16 widescreen:gap-24 xlarge:gap-40 justify-start gap-8",
            carouselContentClassName
          )}
          {...{
            style: {
              "--base-basis": "25vw",
              "--desktop-basis": "13vw",
              "--large-basis": "140px",
              "--tablet-basis": "18vw",
              "--xlarge-basis": "180px",
            },
          }}
          viewportClassName={cn(
            "mask-fade-right large:mask-none widescreen:py-60 tablet:px-32 widescreen:px-60 px-16 py-32",
            viewportClassName
          )}
          {...carouselContentProps}
        >
          {element.items.map((item, i) => {
            const image = {
              ...item.image,
              alt: "",
              sizes:
                "(min-width: 1920px) 180px, (min-width: 1280px) 140px, (min-width: 1024px) 13vw, (min-width: 768px) 18vw, 25vw",
              style: {
                ...defaultBubbleStyle,
                ...item.image?.style,
              },
            }
            const { className, ...titleProps } = item.title_style || {}
            return (
              <CarouselItem
                className={cn(
                  "xlarge:basis-(--xlarge-basis) large:basis-(--large-basis) desktop:basis-(--desktop-basis) basis-(--base-basis) tablet:basis-(--tablet-basis) text-center",
                  carouselItemClassName
                )}
                key={`${element.id}-${item.id}-${i}`}
                {...carouselItemProps}
              >
                <Button
                  asChild
                  className={cn("block h-full")}
                  onClick={() => handleClick(item.title, item.url)}
                >
                  <Link
                    data-lc={element.anchor_name || element.id}
                    to={item.url}
                  >
                    <Image
                      containerStyle={{ px: 1 }}
                      image={image}
                      lazyload={lazyload}
                    />
                    <h2
                      className={cn(
                        "body widescreen:mt-8 tablet:px-8 xlarge:px-16 mt-8 px-12 font-semibold",
                        itemTitleClassName,
                        className
                      )}
                      {...itemTitleProps}
                      {...titleProps}
                    >
                      {item.title}
                    </h2>
                  </Link>
                </Button>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <CarouselPrevious
          className={cn("large:hidden inline-flex", carouselControlsClassName)}
        />
        <CarouselNext
          className={cn("large:hidden inline-flex", carouselControlsClassName)}
        />
      </Carousel>
    </div>
  )
}

LandingCollectionMenu.propTypes = {
  element: PropTypes.object,
  lazyload: PropTypes.bool,
}

export default LandingCollectionMenu
